import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminApi, type BookingStatus, type BookingStatusInput } from '@/lib/adminApi';
import { Card, DataTable, Spinner, EmptyState, type Column } from '@/components/ui';
import { IconLogs } from '@/components/ui/icons';
import { AdminPage, AdminPageHeader, SearchBox } from '@/components/admin/shared';
import { Drawer, Field } from '@/components/admin/Drawer';
import { useAdminPermission } from '@/lib/useAdminPermission';

/** Booking Statuses — the `booking_statuses` lookup table: the internal +
 *  customer-facing label every trip status maps to (Accepted / At Point / Start
 *  Trip / End Trip / Completed / Cancelled By Customer / … ). The id IS the
 *  status code the apps key on, so the list is ordered by id.
 *
 *  READ + AUTHORED here: this is a pure CONFIG/lookup table with NO owning Go
 *  service (trip-svc only JOINs it for display), so — exactly like staff
 *  management — admin-svc owns these writes directly (audited + RBAC-gated
 *  server-side). Staff with booking_statuses.manage (ops + superadmin) can
 *  create / edit / delete a label row; read-only roles still see the list. The
 *  page is DEFENSIVE (shows a "not yet in Postgres" state when the table hasn't
 *  landed pre-cutover). Covers the legacy Filament BookingStatusResource. */
export function BookingStatuses() {
  const [q, setQ] = useState('');
  // null = closed; 'new' = create; a BookingStatus = edit that row.
  const [editing, setEditing] = useState<BookingStatus | 'new' | null>(null);
  const canManage = useAdminPermission('booking_statuses.manage');

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['admin', 'booking-statuses', q],
    queryFn: ({ signal }) => adminApi.bookingStatuses({ q: q || undefined }, signal),
  });

  const unavailable = data && data.available === false;

  return (
    <AdminPage>
      <AdminPageHeader
        title="Booking statuses"
        subtitle="The internal + customer-facing label every trip status maps to."
        right={
          <SearchBox
            value={q}
            onChange={setQ}
            placeholder="Search status / message…"
          />
        }
      />

      {unavailable ? (
        <Card>
          <EmptyState
            title="Booking statuses not yet in Postgres"
            description={
              data?.note ??
              'The booking_statuses table is migrated from the legacy schema and is not in this Postgres yet. This view lights up automatically once it lands.'
            }
            icon={<IconLogs className="h-6 w-6" />}
          />
        </Card>
      ) : (
        <>
          {canManage ? (
            <div className="mb-3 flex justify-end">
              <button type="button" onClick={() => setEditing('new')} className="btn-primary">
                New status
              </button>
            </div>
          ) : null}

          <Card flush>
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Spinner label="Loading booking statuses…" />
              </div>
            ) : isError ? (
              <EmptyState title="Couldn't load booking statuses" description={(error as Error)?.message} />
            ) : (
              <BookingStatusesTable
                rows={data?.items ?? []}
                canManage={canManage}
                onEdit={(b) => setEditing(b)}
              />
            )}
          </Card>
        </>
      )}

      {editing ? (
        <BookingStatusDrawer
          status={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </AdminPage>
  );
}

/** The booking-statuses DataTable. Pulled out so the page body stays readable.
 *  Shows the status code (id), the internal name, and the customer-facing
 *  message; an Edit button for manage roles. */
function BookingStatusesTable({
  rows,
  canManage,
  onEdit,
}: {
  rows: BookingStatus[];
  canManage: boolean;
  onEdit: (b: BookingStatus) => void;
}) {
  const columns: Array<Column<BookingStatus>> = [
    {
      key: 'id',
      header: 'Code',
      cell: (r) => <span className="metric text-xs text-text-muted">#{r.id}</span>,
    },
    {
      key: 'status_name',
      header: 'Status (internal)',
      cell: (r) => <span className="font-medium text-slate-100">{r.status_name}</span>,
    },
    {
      key: 'customer_status_name',
      header: 'Customer-facing',
      cell: (r) =>
        r.customer_status_name ? (
          <span className="text-sm text-slate-300">{r.customer_status_name}</span>
        ) : (
          <span className="text-xs text-text-muted">—</span>
        ),
    },
  ];
  if (canManage) {
    columns.push({
      key: 'actions',
      header: '',
      align: 'right',
      cell: (r) => (
        <button
          type="button"
          onClick={() => onEdit(r)}
          className="rounded-md border border-ink-700 px-2 py-1 text-xs text-brand-400 hover:bg-ink-800"
        >
          Edit
        </button>
      ),
    });
  }

  return (
    <DataTable<BookingStatus>
      columns={columns}
      rows={rows}
      rowKey={(r) => String(r.id)}
      stickyHeader
      empty={
        <EmptyState
          title="No booking statuses"
          description="No status labels yet."
          icon={<IconLogs className="h-6 w-6" />}
        />
      }
    />
  );
}

// ─── booking-status authoring drawer ─────────────────────────

/** The editable form state. Mirrors BookingStatusInput — both label columns are
 *  plain strings the inputs bind to. */
interface FormState {
  status_name: string;
  customer_status_name: string;
}

/** BookingStatusDrawer — the create/edit form. status=null ⇒ create; otherwise
 *  edit that row (seeded from it). On success it invalidates the list and closes.
 *  Manage roles can also delete an existing row.
 *
 *  RBAC: only ops + superadmin hold booking_statuses.manage; this drawer only
 *  ever opens for them (the page gates the New/Edit affordances on the same
 *  hook). admin-svc enforces the matrix server-side and 403s regardless. */
function BookingStatusDrawer({
  status,
  onClose,
}: {
  status: BookingStatus | null;
  onClose: () => void;
}) {
  const isEdit = status !== null;
  const qc = useQueryClient();
  const [form, setForm] = useState<FormState>(() => seedForm(status));
  const [clientError, setClientError] = useState<string | null>(null);

  const invalidate = () => qc.invalidateQueries({ queryKey: ['admin', 'booking-statuses'] });

  const mutation = useMutation({
    mutationFn: (input: BookingStatusInput) =>
      isEdit ? adminApi.updateBookingStatus(status!.id, input) : adminApi.createBookingStatus(input),
    onSuccess: () => {
      invalidate();
      onClose();
    },
  });

  const del = useMutation({
    mutationFn: () => adminApi.deleteBookingStatus(status!.id),
    onSuccess: () => {
      invalidate();
      onClose();
    },
  });

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const onSubmit = () => {
    setClientError(null);
    const built = buildInput(form);
    if ('error' in built) {
      setClientError(built.error);
      return;
    }
    mutation.mutate(built.input);
  };

  const busy = mutation.isPending || del.isPending;

  return (
    <Drawer
      title={isEdit ? `Edit status #${status!.id}` : 'New booking status'}
      subtitle={isEdit ? status!.status_name : 'Define a trip-status label'}
      onClose={onClose}
    >
      <div className="space-y-4">
        {isEdit ? (
          <div>
            <Field label="Code" value={`#${status!.id}`} />
          </div>
        ) : null}

        <Card>
          <div className="space-y-3">
            <TextField
              label="Status name (internal)"
              hint="The internal label staff + the back office see (e.g. At Point)."
              value={form.status_name}
              onChange={(v) => set('status_name', v)}
              placeholder="Accepted"
            />
            <TextField
              label="Customer-facing name"
              hint="The message shown to the customer in the app (e.g. Your driver is on the way)."
              value={form.customer_status_name}
              onChange={(v) => set('customer_status_name', v)}
              placeholder="Your driver is on the way"
            />
          </div>
        </Card>

        <div className="flex items-center gap-3">
          <button type="button" disabled={busy} onClick={onSubmit} className="btn-primary">
            {mutation.isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Create status'}
          </button>
          <button type="button" disabled={busy} onClick={onClose} className="btn-secondary">
            Cancel
          </button>
          {isEdit ? (
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                if (window.confirm('Delete this booking status? This cannot be undone.')) del.mutate();
              }}
              className="ml-auto rounded-md border border-danger/40 px-3 py-1.5 text-xs text-danger hover:bg-danger/10 disabled:opacity-50"
            >
              {del.isPending ? 'Deleting…' : 'Delete'}
            </button>
          ) : null}
        </div>
        {clientError ? <p className="text-xs text-danger">{clientError}</p> : null}
        {mutation.isError ? <p className="text-xs text-danger">{(mutation.error as Error)?.message}</p> : null}
        {del.isError ? <p className="text-xs text-danger">{(del.error as Error)?.message}</p> : null}
      </div>
    </Drawer>
  );
}

/** A labelled text input row used in the booking-status form. */
function TextField({
  label,
  hint,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  hint?: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <div>
      <label className="block text-[11px] text-muted">{label}</label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-1 input"
      />
      {hint ? <p className="mt-0.5 text-[11px] text-muted">{hint}</p> : null}
    </div>
  );
}

/** Seed the form from a row (edit) or empty defaults (create). */
function seedForm(b: BookingStatus | null): FormState {
  if (!b) {
    return { status_name: '', customer_status_name: '' };
  }
  return {
    status_name: b.status_name ?? '',
    customer_status_name: b.customer_status_name ?? '',
  };
}

/** Validate + convert the form into a BookingStatusInput, or return an error
 *  string. Mirrors admin-svc validation (status_name + customer_status_name are
 *  required) so the staffer sees a precise message instantly; the server
 *  re-checks everything (this is a UX nicety, not the boundary). */
function buildInput(f: FormState): { input: BookingStatusInput } | { error: string } {
  const status_name = f.status_name.trim();
  if (!status_name) return { error: 'Status name (internal) is required.' };
  const customer_status_name = f.customer_status_name.trim();
  if (!customer_status_name) return { error: 'Customer-facing name is required.' };

  return {
    input: {
      status_name,
      customer_status_name,
    },
  };
}
